/* ============================================================================
   OB-2240P — BK117 B-2, S/N 7074

   Transcrito del formato M-106 del operador, "Manual de masa y centrado
   MBB-BK 117", edición original Rev. 1, febrero 2026.

   TRES COSAS DE ESTE HELICÓPTERO QUE NO SE PARECEN AL AS350
   ---------------------------------------------------------

   1. El BRAZO DEL COMBUSTIBLE NO ES CONSTANTE. En el AS350 el tanque tiene un
      brazo fijo. Acá va de 4,798 m con el tanque casi vacío a 4,563 m lleno:
      23,5 cm de recorrido. Usar un brazo fijo daría un momento equivocado en
      todo el rango, y el error crece con el combustible embarcado. La tabla de
      abajo, de 46 renglones, es la del propio formato.

   2. NUEVE ESTACIONES DE ASIENTO, cada una con su brazo, de 3,492 m en la
      primera fila a 5,229 m en la última. Cargar los pasajeros como un bloque
      con brazo promedio pierde el centrado real: dos personas atrás no pesan
      igual que dos adelante.

   3. LA ENVOLVENTE DEPENDE DE LA CONFIGURACIÓN. Con carga interna llega a
      3350 kg; con carga externa en gancho, a 3500 kg, y el contorno cambia.

   UN ERROR DEL FORMATO ORIGINAL
   -----------------------------
   La celda del brazo de pasajeros es `=AH31/AF31`: momento sobre peso. Con
   cero pasajeros a bordo eso es 0/0, o sea #DIV/0!, y ese error se propaga al
   momento total y de ahí al CG DE DESPEGUE. En el archivo entregado, la masa
   de despegue se lee (2509,91 kg) pero el CG no: dice #DIV/0!.

   Sumando solo las partidas numéricas, ese vuelo tenía el CG en 4,5496 m.

   La aplicación no reproduce ese error: una fila sin peso aporta momento cero
   y no arrastra a las demás.
   ========================================================================== */

import { CARTA_OGE_BK117_B2 } from "./bk117OgeCarta.js";

/**
 * Brazo del combustible según los kilos a bordo.
 *
 * POR ESCALÓN, NO INTERPOLADO
 * ---------------------------
 * El formato M-106 lo resuelve con `LOOKUP`, que devuelve el brazo del ÚLTIMO
 * renglón menor o igual al buscado. O sea: 77 kg toma el brazo de 70, no un
 * valor entre 70 y 80.
 *
 * Interpolar daría un momento distinto —para los 77 kg del vuelo de
 * referencia, 367,3 en vez de los 369,4 del formato— y la hoja de la
 * aplicación dejaría de cuadrar con la que el operador firma. Se reproduce el
 * criterio del formato, aunque interpolar sea más fino: el objetivo es que los
 * dos papeles digan lo mismo.
 *
 * NOTA SOBRE LOS TANQUES: el formato aplica el brazo a CADA TANQUE por
 * separado (principal y supply), no al total a bordo. Con 63 + 77 kg da
 * 671,7 kg·m; tomando los 140 kg juntos daría 648,9. Se conserva el criterio
 * por tanque.
 */
export const BRAZO_COMBUSTIBLE_BK117 = [
  [0, 4.798], [20, 4.798], [30, 4.798], [50, 4.798], [60, 4.798], [70, 4.798],
  [80, 4.759], [90, 4.715], [100, 4.69], [110, 4.673], [120, 4.658], [130, 4.646],
  [140, 4.635], [150, 4.627], [160, 4.619], [170, 4.613], [180, 4.608], [190, 4.604],
  [200, 4.6], [210, 4.597], [220, 4.594], [230, 4.591], [240, 4.589], [250, 4.587],
  [260, 4.585], [270, 4.583], [280, 4.581], [290, 4.579], [300, 4.577], [310, 4.576],
  [320, 4.575], [330, 4.574], [340, 4.573], [350, 4.572], [360, 4.571], [370, 4.57],
  [380, 4.569], [390, 4.568], [400, 4.567], [410, 4.567], [420, 4.566], [430, 4.565],
  [440, 4.565], [450, 4.564], [460, 4.563], [470, 4.563],
];

/**
 * Brazo del combustible para una cantidad dada, en kilos.
 *
 * Devuelve null si la cantidad no es un número: sin brazo no hay momento, y
 * suponer uno sería inventar el centrado.
 */
export function brazoCombustible(kg, tabla = BRAZO_COMBUSTIBLE_BK117) {
  if (!Number.isFinite(kg) || kg < 0) return null;
  // Escalón, como el LOOKUP del formato: el último renglón <= kg.
  let brazo = tabla[0][1];
  for (const [k, b] of tabla) {
    if (k <= kg) brazo = b;
    else break;
  }
  return brazo;
}

/** Las nueve estaciones de asiento, con su brazo. */
export const ESTACIONES_PAX_BK117 = [
  { key: "fwdL", label: "Delantero izquierdo", brazoM: 3.492 },
  { key: "fwdC", label: "Delantero centro", brazoM: 3.492 },
  { key: "fwdR", label: "Delantero derecho", brazoM: 3.492 },
  { key: "midLfwd", label: "Cabina adelante izq.", brazoM: 4.309 },
  { key: "midLcen", label: "Cabina centro izq.", brazoM: 4.769 },
  { key: "midLaft", label: "Cabina atrás izq.", brazoM: 5.229 },
  { key: "midRfwd", label: "Cabina adelante der.", brazoM: 4.309 },
  { key: "midRcen", label: "Cabina centro der.", brazoM: 4.769 },
  { key: "midRaft", label: "Cabina atrás der.", brazoM: 5.229 },
];

/** Equipo opcional del formato, con su peso y brazo publicados. */
export const EQUIPO_OPCIONAL_BK117 = [
  { key: "cable60", label: "Cable de 60 m (fibra)", pesoKg: 36, brazoM: null, nota: "Brazo no publicado en el formato" },
  { key: "swivel", label: "Electric swivel", pesoKg: 1.81, brazoM: null, nota: "Brazo no publicado en el formato" },
  { key: "helibasket", label: "Helibasket", pesoKg: 38, brazoM: 4.34 },
  { key: "stowageFrame", label: "Stowage cargo frame", pesoKg: 2.5, brazoM: 5.835 },
  { key: "stowageNetAft", label: "Stowage cargo net aft", pesoKg: 2.27, brazoM: 5.835 },
  { key: "cargoNetVert", label: "Vertically mounted cargo net", pesoKg: 2.27, brazoM: 5.016 },
  { key: "mochila", label: "Mochila de supervivencia", pesoKg: 10.8, brazoM: 3.489 },
  { key: "maletin", label: "Maletín de vuelo", pesoKg: 0, brazoM: 3.489 },
  { key: "botiquin", label: "Botiquín de primeros auxilios", pesoKg: 1.6, brazoM: 3.6 },
  { key: "kitMercPel", label: "Kit mercancías peligrosas", pesoKg: 2, brazoM: 3.489 },
  { key: "telSat", label: "Teléfono satelital", pesoKg: 2, brazoM: 3.489 },
  { key: "linternas", label: "Linternas", pesoKg: 0.3, brazoM: 3.3 },
];

export const PERFIL_OB2240P = {
  matricula: "OB-2240P",
  serie: "7074",
  tipo: "BK117 B-2 / B-2D",
  pais: "Perú",

  /** Masa básica vacía y su brazo, del formato M-106. */
  emptyKg: 2133.44,
  emptyArm: 4.682,
  emptyMomentKgM: 9988.766,

  maxInternalKg: 3350,
  maxExternalKg: 3500,

  arms: {
    pilotoCopiloto: 2.867,
    combustible: null, // variable: ver brazoCombustible()
    cabinaInterna: 5.35,
    helibasket: 4.34,
    cargaExterna: null, // gancho: brazo bajo el CG, se trata aparte
  },

  /**
   * Envolvente de CG, del propio formato.
   *
   * Dos contornos, uno por configuración. Los puntos vienen del bloque de
   * filas 64-70 del M-106.
   */
  envelope: {
    interna: [
      [1700, 4.375], [2000, 4.337], [3350, 4.4], [3350, 4.5], [1700, 4.67],
    ],
    externa: [
      [1700, 4.375], [2000, 4.337], [3500, 4.358], [3500, 4.536], [2400, 4.606], [1700, 4.67],
    ],
  },

  fuelDensity: 0.8,
  brazoCombustibleTabla: BRAZO_COMBUSTIBLE_BK117,
  estacionesPax: ESTACIONES_PAX_BK117,
  equipoOpcional: EQUIPO_OPCIONAL_BK117,

  /**
   * Consumo del formato: 210 kg/h.
   *
   * Deducido de la fórmula `=K45*210/60` — minutos de vuelo por 210 sobre 60.
   * Es un valor ÚNICO, no una carta por altitud y temperatura: el formato del
   * operador lo usa así. No reemplaza a la carta del RFM, que sí varía con la
   * condición, y la app tiene que decirlo cuando lo use.
   */
  consumoKgHora: 210,
  consumoEsValorUnico: true,

  procedencia: {
    fuente: "Formato M-106 — Manual de masa y centrado MBB-BK 117, Rev. 1, febrero 2026",
    archivo: "PYB_0B_OB2240P_02JUL26.xlsx",
    verificada: false,
    nota:
      "Datos de peso y balance transcritos del formato del operador. Las cartas " +
      "de performance (OGE, IGE, OEI) siguen sin digitalizar: el formato las " +
      "referencia como «de acuerdo con nomograma» sin publicar valores.",
  },
};


/**
 * Perfil de TIPO para la aplicación, en el formato que ya usa el AS350 B3.
 *
 * El tipo describe lo común a todos los BK117 B-2: envolvente de CG, brazos de
 * estación, límites de masa. El peso vacío NO va acá — es de cada matrícula y
 * sale de su acta de pesaje, igual que en el AS350.
 *
 * Las cartas de performance van en null a propósito. Con null la aplicación
 * responde "sin carta" y manda al RFM; con ceros diría que el peso máximo es
 * cero, y con valores inventados sería peor que las dos cosas.
 */
export const PERFIL_TIPO_BK117_B2 = {
  id: "bk117b2",
  tipo: "Eurocopter BK117 B-2",
  bimotor: true,
  motores: { cantidad: 2, modelo: "Lycoming LTS 101-750B-1" },

  // Genéricos del tipo. Cada matrícula sobrescribe con su pesaje.
  emptyKg: 2133.44,
  emptyArm: 4.682,

  maxInternalKg: 3350,
  maxExternalKg: 3500,
  /* RFM 2.6.2: por debajo de 1700 kg el helicóptero está fuera de límites.
     Es una limitación que solo tiene sentido comprobar por tipo. */
  masaMinimaKg: 1700,
  cgLateralLimites: [
    { hastaKg: 2850, limiteMm: 100 },
    { hastaKg: Infinity, limiteMm: 80 },
  ],

  arms: {
    pilot: 2.867,
    paxFront: 3.492,
    paxRear: 4.769,
    bagLat: 4.34,
    bagRear: 5.835,
    external: 4.34,
    // El brazo del combustible es VARIABLE en este tipo: este valor es solo el
    // del tanque casi vacío, para que la estructura no quede incompleta. El
    // cálculo real usa brazoCombustible().
    fuel: 4.798,
  },
  brazoCombustibleVariable: true,

  fuelDensity: 0.8,
  /**
   * DOS TANQUES, no uno.
   *
   * El AS350 tiene un tanque de 540 L. El BK117 tiene principal y auxiliar
   * (supply), con capacidades distintas, y el formato M-106 los carga por
   * separado porque cada uno tiene su propia lectura.
   *
   * Total 478,5 kg = 598 L a 0,8 kg/L. La aplicación mostraba los 540 L del
   * AS350 para esta matrícula: 58 L menos de los que la aeronave lleva.
   */
  tanques: [
    { key: "principal", label: "Tanque principal", capacidadKg: 401.2 },
    { key: "supply", label: "Tanque supply (auxiliar)", capacidadKg: 77.3 },
  ],
  fuelCapacityKg: 478.5,
  fuelCapacityL: 598.1,

  /* La OGE ya está digitalizada: ver cartas/bk117OgeCarta.js. Las otras siguen
     publicadas como nomograma en el RFM y sin convertir. */
  ogeTable: CARTA_OGE_BK117_B2,
  igeTable: null,
  fuelTable: null,
  oeiHogeTable: null,

  envelope: {
    forward: [[1700, 4.375], [2000, 4.337], [3350, 4.4]],
    rearwardInternal: [[1700, 4.67], [3350, 4.5]],
    rearwardExternal: [[1700, 4.67], [2400, 4.606], [3500, 4.536]],
  },

  /**
   * Compartimentos de carga, con los brazos del formato M-106.
   *
   * `armKey` apunta a `arms`; `brazoM` lo fija directo cuando el formato
   * publica un brazo propio para ese compartimento. Los límites salen del RFM
   * 2.6.3: 250 kg detrás de los asientos traseros y 1200 kg en cabina.
   */
  cargoCompartments: [
    { key: "helibasket", label: "Helibasket", maxKg: 250, brazoM: 4.34 },
    { key: "cabinaInterna", label: "Cabina interna", maxKg: 1200, brazoM: 5.35 },
    { key: "stowageFrame", label: "Stowage cargo (popa)", maxKg: 250, brazoM: 5.835 },
    { key: "cargoNetVert", label: "Red de carga vertical", maxKg: 250, brazoM: 5.016 },
  ],

  /**
   * Estaciones de pasajeros del formato M-106.
   *
   * Nueve asientos, cada uno con su brazo. El AS350 usa dos bloques —delante y
   * atrás— porque su formato lo hace así; el BK117 exige estación por estación:
   * dos personas en la fila de popa mueven el momento 278 kg·m más que dos
   * adelante, y con un bloque promedio eso se pierde.
   */
  estacionesPax: ESTACIONES_PAX_BK117,

  datumNote: "Brazos referidos al datum del MBB-BK 117 según formato M-106.",

  procedencia: {
    fuente: "Formato M-106 del operador + RFM BK117 B-2",
    verificada: false,
    nota: "Peso y balance del formato M-106. Cartas de performance sin digitalizar.",
  },
};

/**
 * Entrada de flota de esta matrícula.
 *
 * Trae TODOS los campos que la aplicación espera de una aeronave, no solo los
 * de peso y balance. La primera versión traía únicamente el pesaje, y el panel
 * de mantenimiento —que lee `tsn`, `csn` y `motor.tsn` sin comprobar—
 * reventaba la aplicación entera al abrir la pestaña.
 *
 * Los contadores van en null, no en cero: cero significaría "aeronave a
 * estrenar con 0 horas", y esta tiene un historial que el operador todavía no
 * cargó. Null significa "no lo sé", que es la verdad.
 */
export const FLOTA_OB2240P = {
  id: "f_ob2240p",
  matricula: "OB-2240P",
  tipo: "BK117 B-2 / B-2D",
  /* ═══ `profileId`, NO `perfilId` ═══

     Estaba escrito `perfilId` y el resto de la aplicación lee `profileId`: con
     OB-2240P seleccionada, la aeronave **no encontraba su perfil** y caía al
     del AS350.

     El peso y balance del bimotor se habría calculado con los brazos del
     monomotor, y el resultado se ve igual de plausible. Nada lo delataba. */
  profileId: "bk117b2",
  serie: "7074",
  pais: "Perú",

  // Peso y balance: del formato M-106.
  pesoVacioKg: 2133.44,
  brazoVacioM: 4.682,
  pesajeFecha: "2026-02-01",
  pesajeReferencia: "Formato M-106, Rev. 1, febrero 2026",
  fuelCapacityKg: 478.5,
  fuelCapacityL: 598.1,

  // Contadores de mantenimiento: pendientes de cargar del status report.
  tsn: null,
  csn: null,
  ldg: null,
  cargas: null,
  motor: { tsn: null, cyNG: null, cyNF: null, modelo: "Lycoming LTS 101-750B-1" },
  estadoInicial: null,
};


/* ==========================================================================
   LÍMITES DEL RFM — BK117 B-2, secciones 2.6 y 2.7

   Estos NO estaban en el formato M-106 del operador y sí en el manual de
   vuelo. Varios son limitaciones de despacho que la aplicación no comprobaba.
   ========================================================================== */

export const LIMITES_BK117_B2 = {
  /** Sección 2.6.1 — el máximo absoluto aprobado. */
  masaMaximaKg: 3350,
  /**
   * Sección 2.6.2 — masa mínima aprobada para vuelo.
   *
   * Un helicóptero DEMASIADO LIVIANO también está fuera de límites. La
   * aplicación solo comprobaba el máximo: un BK117 vacío con poco combustible
   * puede caer por debajo de 1700 kg y pasaba sin aviso.
   */
  masaMinimaKg: 1700,

  /** Sección 2.6.3 — límites de carga en piso y compartimentos. */
  cargaPisoMaxKgM2: 600,
  equipajeDetrasAsientosMaxKg: 250,
  cargaCabinaMaxKg: 1200,
  anillaAmarreMaxKg: 100,
  notaAreaPopa: "El área a popa de STA 5600 (escalón) no se usa para carga ni equipaje: solo artículos livianos o blandos.",

  /**
   * Sección 2.7.2 — límites laterales de centro de gravidad.
   *
   * Cambian con el peso: 100 mm hasta 2850 kg, 80 mm por encima. El formato
   * M-106 no los traía y la aplicación no comprobaba el balance lateral de
   * este tipo en absoluto.
   */
  cgLateral: [
    { hastaKg: 2850, limiteMm: 100 },
    { hastaKg: Infinity, limiteMm: 80 },
  ],

  /** Sección 2.7.1 — el datum está 4000 mm delante del punto de nivelación. */
  datumNota: "Estación cero: plano vertical perpendicular al eje, 4000 mm delante del punto de nivelación.",
};

/** Límite lateral de CG aplicable a un peso bruto dado, en milímetros. */
export function limiteCgLateralMm(pesoBrutoKg, tabla = LIMITES_BK117_B2.cgLateral) {
  if (!Number.isFinite(pesoBrutoKg) || pesoBrutoKg <= 0) return null;
  for (const t of tabla) if (pesoBrutoKg <= t.hastaKg) return t.limiteMm;
  return tabla[tabla.length - 1].limiteMm;
}

/**
 * ¿La masa está dentro de los límites de despacho?
 *
 * Comprueba las DOS puntas. Un helicóptero por debajo de la masa mínima está
 * tan fuera de límites como uno por encima de la máxima, y la aplicación solo
 * miraba el máximo.
 */
export function masaDentroDeLimites(pesoBrutoKg, limites = LIMITES_BK117_B2) {
  if (!Number.isFinite(pesoBrutoKg)) {
    return { dentro: null, motivo: "Sin peso bruto no se puede comprobar." };
  }
  if (pesoBrutoKg > limites.masaMaximaKg) {
    return {
      dentro: false,
      motivo: `${Math.round(pesoBrutoKg)} kg supera la masa máxima aprobada de ${limites.masaMaximaKg} kg (RFM 2.6.1).`,
    };
  }
  if (pesoBrutoKg < limites.masaMinimaKg) {
    return {
      dentro: false,
      motivo: `${Math.round(pesoBrutoKg)} kg está por debajo de la masa mínima aprobada de ${limites.masaMinimaKg} kg (RFM 2.6.2).`,
    };
  }
  return { dentro: true, motivo: null };
}

/**
 * BRAZOS OFICIALES DEL RFM, sección 6.3.
 *
 * El formato M-106 del operador usa brazos por estación individual; el manual
 * publica brazos por GRUPO en su ejemplo de carga. Los dos criterios conviven:
 * el del operador es más fino, el del manual sirve para verificar.
 *
 * El ejemplo del manual cierra exacto —2686 kg, 12 038 846 kg·mm, CG 4482 mm—
 * y se usa como caso de referencia en las pruebas.
 */
export const BRAZOS_RFM_BK117_MM = {
  piloto: 2867,
  copiloto: 2867,
  paxDelanteros: 3726,
  paxTraseros: 4616,
  equipaje: 5494,
  combustibleLleno: 4563,
};

export const EJEMPLO_CARGA_RFM = {
  referencia: "RFM BK117 B-2, sección 6.3",
  partidas: [
    { label: "Masa vacía", kg: 1682, brazoMm: 4642 },
    { label: "Piloto", kg: 80, brazoMm: 2867 },
    { label: "Copiloto", kg: 80, brazoMm: 2867 },
    { label: "Pax delanteros (2)", kg: 160, brazoMm: 3726 },
    { label: "Pax traseros (2)", kg: 160, brazoMm: 4616 },
    { label: "Equipaje", kg: 50, brazoMm: 5494 },
    { label: "Combustible", kg: 474, brazoMm: 4563 },
  ],
  totalKg: 2686,
  momentoKgMm: 12038846,
  cgMm: 4482,
};
